import { useStore } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import {
  getCarcLabelByCode,
  getCarcOptions,
  getFrequentlyUsedCarcCodes,
  getFrequentlyUsedNateraTagCodes,
  getFrequentlyUsedRarcCodes,
  getMappedRarcOptions,
  getNateraTagLabelByCode,
  getNateraTagOptions,
  getRarcLabelByCode,
  getRarcOptions,
} from '~/entities/carc-rarc'
import {
  getCategoryMetadata,
  isAppealPayerRequiredForm,
  isClaimAppealAcknowledgement,
  isClaimAppealRejected,
  useSubcategories,
} from '~/entities/categories'
import {
  DATA_EXTRACTION_FIELDS_CONFIG,
  DOCUMENT_SERVER_STATUS,
  getDocumentStepUrl,
  getDocumentsUrlWithCurrentParams,
  isLastStep,
  isTaggingClassification,
  ProcessingAlert,
  useClassificationReviews,
  useCurrentDocument,
  useDocumentExtractedCarcRarcData,
  useDocumentProcessingPolling,
} from '~/entities/document'
import { FLAG_TYPES, useStepFlagState } from '~/entities/document-flags'
import { useAuditTrailQueryParams } from '~/features/audit-trail'
import { useFirstInteraction, useStepCompletion, useStepView } from '~/features/document-analytics'
import { closePdfTabOrRemind } from '~/features/pdf-tab-reminder'
import {
  useAutoAcceptPatientMatchIndex,
  useSubmitPatientMatchingReview,
} from '~/pages/document-patient-matching/@x/document-tagging'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import { useAppForm } from '~/shared/lib/form'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import { useUndoRedo } from '~/shared/lib/use-undo-redo'
import { MultiSelect, NateraIcon, Text, Textarea } from '~/shared/ui'
import { ReviewActions } from '~/widgets/review-actions'
import { useAddTagsReview } from '../../api'

const FREQUENTLY_USED_CODES_LIMIT = 5
const CLAIM_APPEAL_REJECTED_FREQUENT_RARCS = ['N255', 'N433']
const CLAIM_APPEAL_ACK_ALLOWED_TAGS = [
  'ATAT30',
  'ATAT45',
  'ATAT60',
  'ATAT90',
  'ATAT120',
  'ATAT180',
  'ATAT365',
  'TFNTSP',
]

type TaggingFormValues = Record<
  'carc_codes' | 'rarc_codes' | 'natera_tags' | 'natera_tags_notes',
  string
>

export function TaggingLegacy() {
  useStepView()
  const formRef = useRef<HTMLFormElement>(null)
  useFirstInteraction(formRef)
  const { trackStepCompletion } = useStepCompletion()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    data: { document },
  } = useCurrentDocument()
  useDocumentProcessingPolling()
  const subcategories = useSubcategories()
  const { data: extractedCarcRarcData } = useDocumentExtractedCarcRarcData({
    documentKey: document.key,
  })
  const queryParams = useAuditTrailQueryParams()
  const { mutateAsync: addTagsReview, isError: isTagsReviewError } = useAddTagsReview()
  const isReviewStateResetEnabled = useIsFeatureEnabled('2026-07-review-state-reset')
  const {
    data: { items: classificationReviews },
  } = useClassificationReviews({ documentKey: document.key })
  const isFullyReviewed =
    document.review_status === DOCUMENT_SERVER_STATUS.reviewed ||
    document.review_status === DOCUMENT_SERVER_STATUS.auto_reviewed
  const wasReclassified =
    isReviewStateResetEnabled && (classificationReviews?.length ?? 0) >= 2 && !isFullyReviewed
  const { isScrolled, sentinelElement } = useIsScrolled()
  const stepFlag = useStepFlagState(FLAG_TYPES.STEP_CARC_RARC)

  const categoryMetadata = useMemo(
    () => getCategoryMetadata({ category: document.category, subcategories }),
    [document.category, subcategories],
  )
  const isCarcOptional = isAppealPayerRequiredForm(document.category)
  const isRestrictedTagsEnabled = useIsFeatureEnabled('2026-06-claim-appeal-restricted-tags')
  const isHideLlmRarcCarcEnabled = useIsFeatureEnabled('2026-07-hide-llm-rarc-carc')
  const isLegacyMappingEnabled = useIsFeatureEnabled('2026-07-legacy-carc-rarc-mapping')

  // PBDP-1412: when the patient-matching step already has a deterministic match,
  // completing the tagging step auto-completes patient matching too and returns
  // the reviewer to the list instead of advancing into the matching step.
  const isSkipDeterministicMatchingEnabled = useIsFeatureEnabled(
    '2026-07-skip-deterministic-patient-matching',
  )
  const isTaggingStep = isTaggingClassification(document.category, subcategories)
  const autoAcceptPatientMatchIndex = useAutoAcceptPatientMatchIndex({
    documentKey: document.key,
    enabled: isSkipDeterministicMatchingEnabled && isTaggingStep,
  })
  const shouldSkipPatientMatching =
    isSkipDeterministicMatchingEnabled && autoAcceptPatientMatchIndex !== null
  const { submitPatientMatchingReview } = useSubmitPatientMatchingReview({
    documentKey: document.key,
  })
  const shouldRemindAfterSkip = isLastStep('patient', {
    isTaggingEnabled: isTaggingStep,
    isEob: false,
    isNonFinancialEob: false,
  })

  const hasTaggingBeenReviewed =
    document.reviewed_step === 'carc_rarc' ||
    document.reviewed_step === 'patient' ||
    document.review_status === 'reviewed' ||
    document.review_status === 'auto_reviewed'
  const shouldHideLlmSuggestions =
    isHideLlmRarcCarcEnabled && isTaggingClassification(document.category, subcategories)
  const shouldHideLlmRarcCarc = shouldHideLlmSuggestions && !hasTaggingBeenReviewed

  const serverCarcValues = useMemo(() => {
    const carcCodes = extractedCarcRarcData?.fields.find((field) => field.name === 'carc_codes')
      ?.value as apiClient.CarcRarcCode[] | undefined
    return carcCodes?.map((code) => code.code)
  }, [extractedCarcRarcData?.fields])

  const serverRarcValues = useMemo(() => {
    const rarcCodes = extractedCarcRarcData?.fields.find((field) => field.name === 'rarc_codes')
      ?.value as apiClient.CarcRarcCode[] | undefined
    return rarcCodes?.map((code) => code.code)
  }, [extractedCarcRarcData?.fields])

  const serverNateraTagValues = useMemo(() => {
    const nateraTags = extractedCarcRarcData?.fields.find((field) => field.name === 'natera_tags')
      ?.value as apiClient.TagInfo[] | undefined
    return nateraTags?.map((tag) => tag.code)
  }, [extractedCarcRarcData?.fields])

  const recommendedCarcValues = useMemo(() => {
    const recommended = extractedCarcRarcData?.original_fields_from_llm?.find(
      (field) => field.name === 'carc_codes',
    )?.value as apiClient.CarcRarcCode[] | undefined
    return recommended?.map((code) => code.code) ?? []
  }, [extractedCarcRarcData?.original_fields_from_llm])

  const recommendedRarcValues = useMemo(() => {
    const recommended = extractedCarcRarcData?.original_fields_from_llm?.find(
      (field) => field.name === 'rarc_codes',
    )?.value as apiClient.CarcRarcCode[] | undefined
    return recommended?.map((code) => code.code) ?? []
  }, [extractedCarcRarcData?.original_fields_from_llm])

  const recommendedNateraTagValues = useMemo(() => {
    const recommended = extractedCarcRarcData?.original_fields_from_llm?.find(
      (field) => field.name === 'natera_tags',
    )?.value as apiClient.TagInfo[] | undefined
    return recommended?.map((tag) => tag.code) ?? []
  }, [extractedCarcRarcData?.original_fields_from_llm])

  const resolvedNateraTagValues = useMemo(() => {
    if (shouldHideLlmSuggestions && !hasTaggingBeenReviewed) return undefined
    return serverNateraTagValues
  }, [shouldHideLlmSuggestions, hasTaggingBeenReviewed, serverNateraTagValues])

  const defaultValues = useMemo<TaggingFormValues>(
    () => ({
      carc_codes: shouldHideLlmRarcCarc ? '' : (serverCarcValues?.join(',') ?? ''),
      rarc_codes: shouldHideLlmRarcCarc ? '' : (serverRarcValues?.join(',') ?? ''),
      natera_tags: resolvedNateraTagValues?.join(',') ?? '',
      natera_tags_notes: '',
    }),
    [shouldHideLlmRarcCarc, serverCarcValues, serverRarcValues, resolvedNateraTagValues],
  )

  // Check if tags/carcs/rarcs have changed from initial values to show mandatory comment field
  const getShouldShowMandatoryComment = useCallback(
    (
      carcCodesValue: string | undefined,
      rarcCodesValue: string | undefined,
      nateraTagsValue: string | undefined,
    ) => {
      const currentCarcCodes = carcCodesValue?.split(',').filter(Boolean).sort().join(',') || ''
      const initialCarcCodes = defaultValues.carc_codes.split(',').filter(Boolean).sort().join(',')
      const carcCodesChanged = currentCarcCodes !== initialCarcCodes

      const currentRarcCodes = rarcCodesValue?.split(',').filter(Boolean).sort().join(',') || ''
      const initialRarcCodes = defaultValues.rarc_codes.split(',').filter(Boolean).sort().join(',')
      const rarcCodesChanged = currentRarcCodes !== initialRarcCodes

      const currentTags = nateraTagsValue?.split(',').filter(Boolean).sort().join(',') || ''
      const initialTags = defaultValues.natera_tags.split(',').filter(Boolean).sort().join(',')
      const nateraTagsChanged = currentTags !== initialTags

      return nateraTagsChanged || carcCodesChanged || rarcCodesChanged
    },
    [defaultValues],
  )

  const form = useAppForm({
    defaultValues,
    validators: {
      onChange: ({ value }) => {
        const hasChanges = getShouldShowMandatoryComment(
          value.carc_codes,
          value.rarc_codes,
          value.natera_tags,
        )
        if (hasChanges && !value.natera_tags_notes?.trim()) {
          return {
            fields: {
              natera_tags_notes: 'Please explain why you modified the tags or codes.',
            },
          }
        }
        return undefined
      },
    },
    onSubmit: async ({ value }) => {
      if (!extractedCarcRarcData?.key) return

      const extractedCarcRarcFields = {
        carc_codes:
          value.carc_codes
            ?.split(',')
            .filter(Boolean)
            .map((code) => ({
              type: 'carc' as const,
              code,
              description: getCarcLabelByCode(code) ?? '',
            })) ?? [],
        rarc_codes:
          value.rarc_codes
            ?.split(',')
            .filter(Boolean)
            .map((code) => ({
              type: 'rarc' as const,
              code,
              description: getRarcLabelByCode(code) ?? '',
            })) ?? [],
        natera_tags:
          value.natera_tags
            ?.split(',')
            .filter(Boolean)
            .map((code) => ({
              type: 'natera_tags' as const,
              code,
              description: getNateraTagLabelByCode(code) ?? '',
            })) ?? [],
      }

      await addTagsReview({
        path: {
          key: document.key,
        },
        body: {
          data_key: extractedCarcRarcData.key,
          is_valid: true,
          fields: extractedCarcRarcFields,
          ...(value.natera_tags_notes?.trim() && { notes: value.natera_tags_notes.trim() }),
        },
      })

      queryClient.invalidateQueries(
        apiClient.getDocumentTagsDocumentsKeyTagsGetOptions({
          path: { key: document.key },
        }),
      )

      trackStepCompletion()

      // PBDP-1412: when the match is deterministic, auto-complete the (final)
      // patient-matching step here and return to the list. The tags review POST
      // above only advances `reviewed_step` to `carc_rarc`; this second POST is
      // what flips the document to `reviewed`. Both are awaited so a failure in
      // either surfaces the tagging error and leaves the doc recoverable.
      if (shouldSkipPatientMatching) {
        await submitPatientMatchingReview({
          match_status: 'validated',
          selected_match_index: autoAcceptPatientMatchIndex,
        })
        if (shouldRemindAfterSkip) {
          void closePdfTabOrRemind(document.key)
        }
        // `useSubmitPatientMatchingReview` navigates back to the list on success.
        return
      }

      navigate(getDocumentStepUrl(document.key, 'patient', { queryParams }))
    },
  })

  const {
    set: setHistoryValues,
    reset: resetHistoryValues,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo({
    initialValues: defaultValues,
    setFieldValue: form.setFieldValue,
  })

  const frequentCarcValues = useMemo(
    () => getFrequentlyUsedCarcCodes(categoryMetadata).slice(0, FREQUENTLY_USED_CODES_LIMIT),
    [categoryMetadata],
  )

  const frequentRarcValues = useMemo(() => {
    if (isClaimAppealRejected(categoryMetadata)) {
      return CLAIM_APPEAL_REJECTED_FREQUENT_RARCS
    }
    return getFrequentlyUsedRarcCodes(categoryMetadata).slice(0, FREQUENTLY_USED_CODES_LIMIT)
  }, [categoryMetadata])

  const currentCarcCodes = useStore(form.store, (state) => state.values.carc_codes)

  const mappedRarcOptions = useMemo(() => {
    if (!isLegacyMappingEnabled) return null
    const selectedCarcs = currentCarcCodes?.split(',').filter(Boolean) ?? []
    if (selectedCarcs.length === 0) return null

    const allMappedRarcs = new Set<string>()
    for (const carcCode of selectedCarcs) {
      const result = getMappedRarcOptions(carcCode, serverRarcValues)
      if (result === null) return null
      if (result.options.length === 0 && !result.rarcRequired) return null
      for (const opt of result.options) {
        allMappedRarcs.add(opt.value)
      }
    }

    const baseOptions = getRarcOptions(serverRarcValues)
    let options = baseOptions.filter((opt) => allMappedRarcs.has(opt.value))

    if (serverRarcValues) {
      const mappedValues = new Set(options.map((o) => o.value))
      const hiddenServerOptions = baseOptions
        .filter((opt) => serverRarcValues.includes(opt.value) && !mappedValues.has(opt.value))
        .map((opt) => ({ ...opt, hidden_from_search: true }))
      options = [...options, ...hiddenServerOptions]
    }

    if (isClaimAppealRejected(categoryMetadata)) {
      const existingValues = new Set(options.map((o) => o.value))
      const extraOptions = baseOptions.filter(
        (opt) =>
          CLAIM_APPEAL_REJECTED_FREQUENT_RARCS.includes(opt.value) &&
          !existingValues.has(opt.value),
      )
      options = [...options, ...extraOptions]
    }

    return options
  }, [isLegacyMappingEnabled, currentCarcCodes, serverRarcValues, categoryMetadata])

  const frequentNateraTagValues = useMemo(
    () => getFrequentlyUsedNateraTagCodes(categoryMetadata).slice(0, FREQUENTLY_USED_CODES_LIMIT),
    [categoryMetadata],
  )

  const nateraTagOptions = useMemo(
    () => getNateraTagOptions(categoryMetadata, serverNateraTagValues),
    [categoryMetadata, serverNateraTagValues],
  )

  const filteredNateraTagOptions = useMemo(() => {
    if (isRestrictedTagsEnabled && isClaimAppealAcknowledgement(document.category)) {
      const allowedOptions = nateraTagOptions.filter((opt) =>
        CLAIM_APPEAL_ACK_ALLOWED_TAGS.includes(opt.value),
      )
      const serverOnlyOptions = nateraTagOptions
        .filter(
          (opt) =>
            !CLAIM_APPEAL_ACK_ALLOWED_TAGS.includes(opt.value) &&
            serverNateraTagValues?.includes(opt.value),
        )
        .map((opt) => ({ ...opt, hidden_from_search: true }))
      return [...allowedOptions, ...serverOnlyOptions]
    }
    return nateraTagOptions
  }, [nateraTagOptions, isRestrictedTagsEnabled, document.category, serverNateraTagValues])

  return (
    <form
      ref={formRef}
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {document.review_status === DOCUMENT_SERVER_STATUS.processing && (
        <ProcessingAlert className="mx-6 mt-4" />
      )}
      <div className="flex flex-col gap-6 px-6 py-4">
        <div>
          <div className="flex items-center gap-3">
            <Text appearance="SUBTITLE2">Adjustment & Remark Codes</Text>
          </div>
          <div>
            <Text
              className="mt-1 mb-4 block"
              appearance="CAPTION"
            >
              Details on Claim Adjustments and Remittance Remarks provided by the payer.
            </Text>
          </div>
          <div className="mb-1 flex flex-col gap-4">
            <form.Field
              name="carc_codes"
              validators={{
                onChange: ({ value }) => {
                  if (!isCarcOptional && !value) {
                    return 'Required'
                  }
                },
                onMount: ({ value }) => {
                  if (!isCarcOptional && !value) {
                    return 'Required'
                  }
                },
              }}
            >
              {(field) => (
                <MultiSelect
                  label={DATA_EXTRACTION_FIELDS_CONFIG.carc_codes.label}
                  placeholder={DATA_EXTRACTION_FIELDS_CONFIG.carc_codes.placeholder}
                  hint={DATA_EXTRACTION_FIELDS_CONFIG.carc_codes.hint}
                  required={!isCarcOptional}
                  options={getCarcOptions(serverCarcValues)}
                  frequentValues={frequentCarcValues}
                  frequentGroupLabel="Frequently Used AMD and x12 CARCs"
                  recommendedValues={shouldHideLlmSuggestions ? [] : recommendedCarcValues}
                  recommendedGroupLabel="opti Suggested CARCs"
                  value={field.state.value ? field.state.value.split(',') : []}
                  onValueChange={(value) => {
                    const fieldValue = value.join(',')
                    field.handleChange(fieldValue)
                    if (value.length === 0) {
                      field.form.setFieldValue('rarc_codes', '')
                      setHistoryValues({
                        [field.name]: fieldValue,
                        rarc_codes: '',
                      })
                    } else if (isLegacyMappingEnabled) {
                      const allMappedRarcs = new Set<string>()
                      const autoSelectRarcs = new Set<string>()
                      let hasUnmapped = false
                      for (const carcCode of value) {
                        const result = getMappedRarcOptions(carcCode, serverRarcValues)
                        if (result === null) {
                          hasUnmapped = true
                          continue
                        }
                        if (result.options.length === 0 && !result.rarcRequired) {
                          hasUnmapped = true
                          continue
                        }
                        for (const opt of result.options) {
                          allMappedRarcs.add(opt.value)
                        }
                        if (result.rarcRequired && result.options.length === 1) {
                          autoSelectRarcs.add(result.options[0].value)
                        }
                      }

                      const currentRarcs =
                        field.form.state.values.rarc_codes?.split(',').filter(Boolean) ?? []
                      const validRarcs = hasUnmapped
                        ? [...currentRarcs]
                        : currentRarcs.filter((r) => allMappedRarcs.has(r))
                      for (const rarc of autoSelectRarcs) {
                        if (!validRarcs.includes(rarc)) {
                          validRarcs.push(rarc)
                        }
                      }
                      const newRarcValue = validRarcs.join(',')
                      if (newRarcValue !== field.form.state.values.rarc_codes) {
                        field.form.setFieldValue('rarc_codes', newRarcValue)
                        setHistoryValues({
                          [field.name]: fieldValue,
                          rarc_codes: newRarcValue,
                        })
                      } else {
                        setHistoryValues({ [field.name]: fieldValue })
                      }
                    } else {
                      setHistoryValues({
                        [field.name]: fieldValue,
                      })
                    }
                  }}
                  error={
                    field.state.meta.isTouched ? field.state.meta.errors.join(', ') : undefined
                  }
                />
              )}
            </form.Field>
            <form.Field name="rarc_codes">
              {(field) => (
                <MultiSelect
                  label={
                    !form.state.values.carc_codes
                      ? 'Choose a CARC to enable RARC selection'
                      : DATA_EXTRACTION_FIELDS_CONFIG.rarc_codes.label
                  }
                  placeholder={DATA_EXTRACTION_FIELDS_CONFIG.rarc_codes.placeholder}
                  hint={DATA_EXTRACTION_FIELDS_CONFIG.rarc_codes.hint}
                  disabled={!form.state.values.carc_codes}
                  options={mappedRarcOptions ?? getRarcOptions(serverRarcValues)}
                  frequentValues={frequentRarcValues}
                  frequentGroupLabel="Frequently Used AMD and x12 RARCs"
                  recommendedValues={shouldHideLlmSuggestions ? [] : recommendedRarcValues}
                  recommendedGroupLabel="opti Suggested RARCs"
                  value={field.state.value ? field.state.value.split(',') : []}
                  onValueChange={(value) => {
                    const newRarcValue = value.join(',')
                    field.handleChange(newRarcValue)
                    setHistoryValues({ rarc_codes: newRarcValue })
                  }}
                />
              )}
            </form.Field>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3">
            <span className="bg-surface-medium flex size-6 items-center justify-center rounded-full">
              <NateraIcon size="18" />
            </span>
            <Text appearance="SUBTITLE2">Natera Tags</Text>
          </div>
          <div>
            <Text
              className="mt-1 mb-4 block"
              appearance="CAPTION"
            >
              Select options that are relevant to the content in the document to improve routing and
              downstream workflows.
            </Text>
          </div>
          <div className="mb-1 flex flex-col gap-4">
            <form.Field name="natera_tags">
              {(field) => (
                <MultiSelect
                  label={DATA_EXTRACTION_FIELDS_CONFIG.natera_tags.label}
                  placeholder={DATA_EXTRACTION_FIELDS_CONFIG.natera_tags.placeholder}
                  required={false}
                  options={filteredNateraTagOptions}
                  frequentValues={frequentNateraTagValues}
                  frequentGroupLabel="Frequently Used Natera Tags"
                  recommendedValues={shouldHideLlmRarcCarc ? [] : recommendedNateraTagValues}
                  recommendedGroupLabel="opti Suggested Natera Tags"
                  value={field.state.value ? field.state.value.split(',') : []}
                  onValueChange={(value) => {
                    const fieldValue = value.join(',')
                    field.handleChange(fieldValue)
                    setHistoryValues({
                      [field.name]: fieldValue,
                    })
                  }}
                  error={
                    field.state.meta.isTouched ? field.state.meta.errors.join(', ') : undefined
                  }
                />
              )}
            </form.Field>
          </div>
        </div>

        <form.Subscribe
          selector={(state) => ({
            carc_codes: state.values.carc_codes,
            rarc_codes: state.values.rarc_codes,
            natera_tags: state.values.natera_tags,
          })}
        >
          {({ carc_codes, rarc_codes, natera_tags }) => {
            const shouldShowNotes = getShouldShowMandatoryComment(
              carc_codes,
              rarc_codes,
              natera_tags,
            )
            if (!shouldShowNotes) return null
            return (
              <form.Field name="natera_tags_notes">
                {(field) => (
                  <div className="flex flex-col gap-2">
                    <Text
                      className="text-alert-error-content"
                      appearance="OVERLINE"
                    >
                      Comments
                    </Text>
                    <Text appearance="CAPTION">
                      Help us understand why you've made a change to the suggested codes or tags.
                      This will support us in training opti so that the suggestions are more
                      accurate in the future.
                      <span className="text-error-main">*</span>
                    </Text>
                    <Textarea
                      className="h-[64px] resize-none"
                      placeholder="Add your thoughts here."
                      filled
                      value={field.state.value}
                      onChange={(e) => {
                        field.handleChange(e.target.value)
                        setHistoryValues({ natera_tags_notes: e.target.value })
                      }}
                    />
                  </div>
                )}
              </form.Field>
            )
          }}
        </form.Subscribe>
      </div>
      <form.Subscribe
        selector={(state) => ({
          isDirty: state.isDirty,
          isSubmitting: state.isSubmitting,
          isValid: state.isValid,
          values: state.values,
          isDefaultValue: state.isDefaultValue,
        })}
      >
        {({ isDirty, isSubmitting, isValid, values, isDefaultValue }) => (
          <ReviewActions
            isDirty={isDirty}
            isSubmitDisabled={
              stepFlag.hasActiveStepFlag
                ? false
                : isSubmitting ||
                  !isValid ||
                  document.review_status === DOCUMENT_SERVER_STATUS.processing ||
                  (!wasReclassified && hasTaggingBeenReviewed && isDefaultValue)
            }
            onSubmit={
              stepFlag.hasActiveStepFlag
                ? () => navigate(getDocumentsUrlWithCurrentParams())
                : form.handleSubmit
            }
            submitLabel={stepFlag.hasActiveStepFlag ? 'Return to Work Queue' : 'Validate'}
            submitTooltip={
              stepFlag.hasActiveStepFlag
                ? undefined
                : getShouldShowMandatoryComment(
                      values.carc_codes,
                      values.rarc_codes,
                      values.natera_tags,
                    ) && !values.natera_tags_notes?.trim()
                  ? 'Enter a brief explanation to validate.'
                  : !isValid
                    ? 'Fill all the required fields.'
                    : undefined
            }
            canUndo={canUndo}
            canRedo={canRedo}
            onRevert={() => {
              form.reset()
              resetHistoryValues()
            }}
            onUndo={undo}
            onRedo={redo}
            previousStepUrl={getDocumentStepUrl(document.key, 'data', {
              queryParams,
            })}
            previousStepLabel="Go to data extraction"
            isError={isTagsReviewError}
            errorContent="opti ran into a problem while saving the tags. Please try again."
            isScrolled={isScrolled}
          />
        )}
      </form.Subscribe>
      {sentinelElement}
    </form>
  )
}
